import { ArticleFooterAuthorInfoProps } from "design-system/components/pages/article-page/article-footer/article-footer-author-info";
import { ArticleFooterLinkedAuthorProps } from "design-system/components/pages/article-page/article-footer/article-footer-linked-author";
import { createContentfulProps } from "frontend/contentful/lib/create-contentful-props";
import { documentToString } from "frontend/contentful/lib/document-to-string";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import {
  OrganizationEntry,
  PersonEntry,
  PersonWrapperEntry,
} from "frontend/contentful/schema/semantics";
import { useContentful } from "frontend/hooks/use-contentful";
import { createPersonImageProps } from "./media-asset.props";

export const createArticleAuthorProps = createContentfulProps<
  PersonEntry | OrganizationEntry | PersonWrapperEntry,
  ArticleFooterAuthorInfoProps
>(({ entry: entity, createEditAttributes }) => {
  const { getEntryUrl, getEntry, getEntityName } = useContentful();
  let name, link, email, phone;

  const person = isContentType<PersonWrapperEntry>("personWrapper", entity)
    ? getEntry(entity.fields.person)
    : entity;

  if (!person) return;

  name = getEntityName(person);
  link =
    "bioURL" in person.fields && person.fields.bioURL
      ? getEntryUrl(person.fields.bioURL)
      : undefined;
  email = "email" in person.fields ? person.fields.email : undefined;
  phone = "phone" in person.fields ? person.fields.phone : undefined;

  // Override with Person Wrapper Entry
  if (isContentType<PersonWrapperEntry>("personWrapper", entity)) {
    name = getEntityName(entity);
    link =
      "bioURL" in entity.fields && entity.fields.bioURL
        ? getEntryUrl(entity.fields.bioURL)
        : link;
    email = "email" in entity.fields ? entity.fields.email : undefined;
    phone = "phone" in entity.fields ? entity.fields.phone : undefined;
  }

  const props: ArticleFooterAuthorInfoProps = {
    name,
    link,
    email,
    phone,
    editAttributes: {
      name: createEditAttributes({
        entry: person,
        fieldId: isContentType<PersonEntry>("person", person)
          ? "firstName"
          : "name",
      }),
      email:
        "email" in person.fields
          ? createEditAttributes({
              entry: person,
              fieldId: "email",
            })
          : undefined,
      phone:
        "phone" in person.fields
          ? createEditAttributes({
              entry: person,
              fieldId: "phone",
            })
          : undefined,
    },
  };

  return props;
});

export const createArticleLinkedAuthorProps = createContentfulProps<
  PersonEntry | OrganizationEntry | PersonWrapperEntry,
  ArticleFooterLinkedAuthorProps
>(({ entry: entity }) => {
  const { getEntryUrl, getEntityName, getEntry } = useContentful();

  let entry: PersonEntry | OrganizationEntry | PersonWrapperEntry | undefined =
    undefined;
  let bioUrl: string | undefined = undefined;

  if (isContentType<PersonWrapperEntry>("personWrapper", entity)) {
    entry = getEntry(entity.fields.person);

    if (entity.fields.bioURL) {
      const url = getEntry(entity.fields.bioURL)?.fields.url;
      bioUrl = url?.startsWith("http") ? url : `https://${url}`;
    }
  } else {
    entry = entity;
  }

  if (!entry) return;

  const props: ArticleFooterLinkedAuthorProps = {
    name: getEntityName(entry),
    href:
      "bioURL" in entry.fields && entry.fields.bioURL
        ? getEntryUrl(entry.fields.bioURL)
        : bioUrl,
    titles: isContentType<PersonEntry>("person", entry)
      ? entry.fields.jobTitles
      : undefined,
    bio:
      "bio" in entry.fields && entry.fields.bio
        ? documentToString(entry.fields.bio)
        : undefined,
    image: isContentType<PersonEntry>("person", entry)
      ? createPersonImageProps(entry)?.image
      : undefined,
  };

  return props;
});
